import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Heart, Share2, Download, ChevronLeft, ChevronRight, UserPlus, UserCheck, Wand2, Upload, Gift, BadgeCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import PhotoTipDialog from './PhotoTipDialog';

/**
 * Full-screen lightbox for Discover creations.
 * Props:
 *   items: creation list (same shape as feed items)
 *   startIndex: index to open at
 *   onClose(): void
 *   onLike(id): optional
 */
export default function CreationLightbox({ items, startIndex = 0, onClose, onLike, onPublish, showOwnerActions = false, onDeleted }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [idx, setIdx] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [followed, setFollowed] = useState({}); // creator_id → bool
  const [followBusy, setFollowBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const touchStart = useRef(null);
  const pinchStart = useRef(null);
  const dragStart = useRef(null);

  useEffect(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, [idx]);

  useEffect(() => {
    // lock background scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx(i => Math.min(items.length - 1, i + 1));
      if (e.key === 'ArrowLeft')  setIdx(i => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
    // eslint-disable-next-line
  }, [items.length]);

  const current = items[idx];

  // Fetch follow state for the current creator (lazy, cached per creator_id)
  useEffect(() => {
    const uname = current?.creator?.username;
    const cid = current?.creator?.id;
    if (!user || !uname || !cid || cid === user.id) return;
    if (followed[cid] !== undefined) return;
    api.get(`/users/${uname}/is_following`)
       .then(r => setFollowed(f => ({ ...f, [cid]: !!r.data.following })))
       .catch(() => {});
    // eslint-disable-next-line
  }, [idx, user, current?.creator?.id]);

  if (!current) return null;

  const toggleFollow = async () => {
    if (!user) { toast.error('Sign in to follow'); return; }
    const cid = current.creator?.id;
    if (!cid) return;
    setFollowBusy(true);
    try {
      const { data } = await api.post('/follow', { target_user_id: cid });
      setFollowed(f => ({ ...f, [cid]: !!data.following }));
      toast.success(data.following ? `Following @${current.creator.username}` : `Unfollowed @${current.creator.username}`);
    } catch { toast.error('Follow failed'); }
    finally { setFollowBusy(false); }
  };

  const next = () => setIdx(i => Math.min(items.length - 1, i + 1));
  const prev = () => setIdx(i => Math.max(0, i - 1));

  const dist = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchStart.current = { d: dist(e.touches[0], e.touches[1]), z: zoom };
    } else if (e.touches.length === 1) {
      touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
      dragStart.current = { x: offset.x, y: offset.y };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStart.current) {
      const d = dist(e.touches[0], e.touches[1]);
      const z = Math.max(1, Math.min(4, pinchStart.current.z * (d / pinchStart.current.d)));
      setZoom(z);
    } else if (e.touches.length === 1 && zoom > 1 && touchStart.current && dragStart.current) {
      // Drag within zoomed image
      const dx = e.touches[0].clientX - touchStart.current.x;
      const dy = e.touches[0].clientY - touchStart.current.y;
      setOffset({ x: dragStart.current.x + dx, y: dragStart.current.y + dy });
    }
  };
  const onTouchEnd = (e) => {
    if (pinchStart.current) { pinchStart.current = null; return; }
    if (!touchStart.current) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStart.current.x;
    const dy = (e.changedTouches[0]?.clientY ?? 0) - touchStart.current.y;
    const dt = Date.now() - touchStart.current.t;
    touchStart.current = null;
    if (zoom > 1) return; // ignore swipe when zoomed
    if (dt < 500 && Math.abs(dy) > 90 && Math.abs(dy) > Math.abs(dx) * 1.3) {
      onClose(); return;
    }
    if (dt < 500 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.3) {
      if (dx < 0) next(); else prev();
    }
  };

  const toggleZoom = () => {
    if (zoom > 1) { setZoom(1); setOffset({ x: 0, y: 0 }); }
    else setZoom(2);
  };

  const share = async () => {
    // Share a public deep-link, NOT the raw image bytes URL (which may be a
    // relative `/api/creations/.../image` proxy — navigator.share rejects relative URLs).
    const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
    const uname = current.creator?.username;
    const shareUrl = uname ? `${origin}/profile/${uname}` : origin;
    const title = `Jixxeo AI creation by @${uname || 'jixxeoai'}`;
    const text = current.prompt
      ? `"${current.prompt.slice(0, 160)}" — made on Jixxeo AI`
      : 'Check out this creation on Jixxeo AI';
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        toast.success('Shared');
      } catch (e) {
        // User-cancelled share throws AbortError — don't nag with an error toast.
        if (e?.name !== 'AbortError') toast.error('Could not open share sheet');
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied — paste it anywhere');
    } catch {
      toast.error('Copy failed — long-press the address bar to share manually');
    }
  };

  const downloadCreation = async () => {
    // Watermarked server download — works for every logged-in user.
    // On iOS the standard <a download> is unreliable (Safari sometimes ignores it)
    // so we prefer Web Share API with files when the device supports it, and fall
    // back to opening the blob URL in a new tab so the user can long-press → Save.
    if (!user) {
      toast.error('Sign in to download');
      return;
    }
    if (!current.id) {
      try { window.open(current.url, '_blank', 'noopener'); } catch {}
      return;
    }
    const isVideo = current.type === 'video';
    const endpoint = isVideo
      ? `/videos/download/${current.id}`
      : `/images/download/${current.id}`;
    setDownloading(true);
    try {
      // Give the user instant feedback — videos take ~10 s to watermark + stream.
      toast.message(isVideo ? 'Preparing watermarked video (10–20 s)…' : 'Preparing watermarked image…');
      const resp = await api.get(endpoint, { responseType: 'blob', timeout: 180000 });
      const blob = resp.data;
      const cd = resp.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/.exec(cd);
      const ext = isVideo ? 'mp4' : 'jpg';
      const mime = isVideo ? 'video/mp4' : 'image/jpeg';
      const fallbackName = `jixxeo-ai_${(current.id || 'creation').slice(0, 12)}.${ext}`;
      const filename = match ? match[1] : fallbackName;

      // iOS Safari / PWA: Web Share API with files triggers the native "Save Video"
      // or "Save to Files" sheet — far more reliable than <a download>.
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOS && navigator.canShare) {
        try {
          const file = new File([blob], filename, { type: mime });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: filename });
            toast.success('Save sheet opened — tap "Save Video" / "Save to Files"');
            setDownloading(false);
            return;
          }
        } catch (shareErr) {
          if (shareErr?.name === 'AbortError') { setDownloading(false); return; }
          // Fall through to blob-URL path below.
        }
      }

      // Universal path: object URL + anchor click. On iOS Safari (no share-files
      // support) we also open the blob URL in a new tab so long-press → Save works.
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (isIOS && isVideo) {
        // Give iOS a second chance — open blob in new tab so user can long-press → Save Video.
        try { window.open(href, '_blank', 'noopener'); } catch {}
        toast.success('Video opened in a new tab — long-press the player and tap "Save Video"');
      } else {
        toast.success(isVideo
          ? 'Video download started (Jixxeo AI watermark baked in)'
          : 'Saved with Jixxeo AI watermark');
      }
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (e) {
      const status = e?.response?.status;
      const msg = status === 403
        ? 'This creation is private — only the owner can download'
        : status === 404
          ? 'Creation not found'
          : status === 502
            ? 'Source video was unreachable — try again in a moment'
            : status === 500
              ? 'Watermark processing failed — refresh and try again'
              : (e?.code === 'ECONNABORTED' ? 'Download timed out — try again on a stable connection' : 'Download failed — try again');
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const isOwner = !!(user && current.creator?.id && user.id === current.creator.id) || showOwnerActions;

  const editRegenerate = () => {
    // Pre-fill Image Studio with this prompt so the user can tweak + regenerate.
    const prompt = current.prompt || '';
    try { sessionStorage.setItem('jixxeo_image_prefill', JSON.stringify({ prompt, source_id: current.id })); } catch {}
    onClose && onClose();
    navigate(`/ai/image?prefill=${encodeURIComponent(prompt).slice(0, 400)}`);
  };

  const publishCreation = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      await api.post(`/creations/${current.id}/publish`);
      toast.success('Published to your profile');
      onPublish && onPublish(current.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
    >
      {/* Header - Mobile-first large touch targets with explicit button type */}
      <div className="relative z-20 flex items-center justify-between p-2 sm:p-4 gap-1 flex-shrink-0"
           style={{ touchAction: 'manipulation' }}>
        <div className="text-xs text-slate-400 font-semibold px-2">{idx + 1} / {items.length}</div>
        <div className="flex items-center gap-0.5 flex-wrap justify-end">
          {isOwner && current.type !== 'video' && (
            <button
              type="button"
              data-testid="lightbox-edit"
              onClick={editRegenerate}
              className="min-w-[44px] min-h-[44px] px-3 py-2 inline-flex items-center justify-center gap-1 rounded-lg text-white text-xs font-bold hover:bg-white/10 active:bg-white/20 transition-colors touch-manipulation"
              title="Edit prompt and regenerate"
            >
              <Wand2 className="w-5 h-5" /> <span className="hidden sm:inline">Edit</span>
            </button>
          )}
          {isOwner && !current.published && (
            <button
              type="button"
              data-testid="lightbox-publish"
              onClick={publishCreation}
              disabled={publishing}
              className="min-w-[44px] min-h-[44px] px-3 py-2 inline-flex items-center justify-center gap-1 rounded-lg text-white text-xs font-bold hover:bg-white/10 active:bg-white/20 transition-colors touch-manipulation disabled:opacity-50"
              title="Publish to your profile"
            >
              <Upload className="w-5 h-5" /> <span className="hidden sm:inline">{publishing ? 'Publishing…' : 'Publish'}</span>
            </button>
          )}
          {onLike && (
            <button
              type="button"
              data-testid="lightbox-like-top"
              onClick={() => onLike(current.id)}
              className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg text-pink-300 hover:bg-pink-500/15 active:bg-pink-500/25 touch-manipulation"
              title="Like"
            >
              <Heart className="w-5 h-5" />
            </button>
          )}
          {!isOwner && user && current.creator?.username ? (
            <button
              type="button"
              data-testid="lightbox-tip"
              onClick={() => {
                toast.message(`Opening tip to @${current.creator.username}…`);
                setTipOpen(true);
              }}
              className="min-w-[44px] min-h-[44px] px-3 py-2 inline-flex items-center justify-center gap-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 active:bg-amber-500/40 text-amber-100 text-xs font-bold touch-manipulation"
              title="Tip the creator"
            >
              <Gift className="w-5 h-5" /> <span className="hidden sm:inline">Tip</span>
            </button>
          ) : (
            <button
              type="button"
              data-testid="lightbox-tip-disabled"
              onClick={() => {
                if (!user) return toast.error('Sign in to tip creators');
                if (isOwner) return toast.message("That's your creation — share it so fans can tip you!");
                if (!current.creator?.username) return toast.error("This creation has no creator attached.");
              }}
              className="min-w-[44px] min-h-[44px] px-3 py-2 inline-flex items-center justify-center gap-1 rounded-lg bg-white/5 text-slate-500 text-xs font-bold touch-manipulation"
              title={isOwner ? 'Your own creation' : (!user ? 'Sign in to tip' : 'No creator info')}
            >
              <Gift className="w-5 h-5" /> <span className="hidden sm:inline">Tip</span>
            </button>
          )}
          <button
            type="button"
            data-testid="lightbox-share"
            onClick={share}
            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg text-white hover:bg-white/10 active:bg-white/20 touch-manipulation"
            title="Share"
          >
            <Share2 className="w-5 h-5" />
          </button>
          <button
            type="button"
            data-testid="lightbox-download"
            onClick={downloadCreation}
            disabled={downloading}
            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg text-white hover:bg-white/10 active:bg-white/20 touch-manipulation disabled:opacity-50"
            title={current.type === 'video' ? 'Download video (watermarked)' : 'Download image (watermarked)'}
          >
            <Download className={`w-5 h-5 ${downloading ? 'animate-pulse' : ''}`} />
          </button>
          <button
            type="button"
            data-testid="lightbox-close"
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg text-white hover:bg-white/10 active:bg-white/20 touch-manipulation"
            title="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 flex items-center justify-center overflow-hidden relative select-none"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {idx > 0 && (
          <button data-testid="lightbox-prev" onClick={prev}
            className="hidden sm:flex absolute left-2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center text-white">
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        {current.type === 'video' ? (
          <video
            data-testid="lightbox-video"
            src={current.url}
            poster={current.poster_url || undefined}
            controls autoPlay playsInline
            className="max-w-full max-h-full rounded-xl"
          />
        ) : (
          <img
            data-testid="lightbox-image"
            src={current.url}
            alt={current.prompt}
            onDoubleClick={toggleZoom}
            draggable={false}
            className="max-w-full max-h-full object-contain select-none transition-transform duration-150"
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`, touchAction: 'none', cursor: zoom > 1 ? 'grab' : 'zoom-in' }}
          />
        )}
        {idx < items.length - 1 && (
          <button data-testid="lightbox-next" onClick={next}
            className="hidden sm:flex absolute right-2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center text-white">
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="relative z-10 p-3 sm:p-4 bg-gradient-to-t from-black/80 to-transparent">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {current.creator?.username ? (
                <Link
                  to={`/profile/${current.creator.username}`}
                  onClick={onClose}
                  data-testid="lightbox-creator-name"
                  className="text-sm font-semibold truncate hover:text-violet-300 transition-colors underline-offset-2 hover:underline"
                >
                  {current.creator?.display_name || current.creator?.username}
                </Link>
              ) : (
                <div className="text-sm font-semibold truncate">
                  {current.creator?.display_name || 'Jixxeo creator'}
                </div>
              )}
              {user && current.creator?.id && current.creator.id !== user.id && (
                <button
                  data-testid="lightbox-follow"
                  onClick={toggleFollow}
                  disabled={followBusy}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors
                    ${followed[current.creator.id]
                      ? 'bg-white/10 border-white/20 text-slate-200'
                      : 'gradient-brand text-white border-transparent shadow'}`}
                >
                  {followed[current.creator.id]
                    ? <><UserCheck className="w-3 h-3" /> Following</>
                    : <><UserPlus className="w-3 h-3" /> Follow</>}
                </button>
              )}
            </div>
            {current.creator?.username && (
              <Link
                to={`/profile/${current.creator.username}`}
                onClick={onClose}
                data-testid="lightbox-creator-handle"
                className="text-xs text-slate-400 truncate hover:text-violet-300 transition-colors block"
              >
                @{current.creator.username}
              </Link>
            )}
            <div className="text-xs text-slate-300 line-clamp-2 mt-1">{current.prompt}</div>
          </div>
          {onLike && (
            <button
              data-testid="lightbox-like"
              onClick={() => onLike(current.id)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-pink-500/20 border border-pink-500/40 text-pink-200 hover:bg-pink-500/30 transition-colors flex-shrink-0"
            >
              <Heart className="w-4 h-4" /> {current.likes || 0}
            </button>
          )}
        </div>
        <div className="text-center text-[10px] text-slate-500 mt-2 sm:hidden">Swipe left/right · Pinch to zoom · Swipe down to close</div>
      </div>

      {/* Mobile sticky action bar — guarantees Publish/Tip/Share/Download are always one tap away */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-30 bg-slate-950/95 backdrop-blur border-t border-white/10 px-2 pt-2 pb-2 space-y-2"
           style={{ touchAction: 'manipulation', paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}>
        {/* Owner-only Publish row — makes it crystal-clear where to publish photos */}
        {isOwner && (
          current.published ? (
            <div
              data-testid="lightbox-mobile-published"
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 bg-emerald-500/15 text-emerald-200 font-bold text-sm border border-emerald-500/30"
            >
              <BadgeCheck className="w-4 h-4" /> Published to Discover
            </div>
          ) : (
            <button
              type="button"
              data-testid="lightbox-mobile-publish"
              onClick={publishCreation}
              disabled={publishing}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-bold text-sm active:scale-95 touch-manipulation disabled:opacity-60 shadow-lg shadow-violet-900/30"
            >
              <Upload className="w-4 h-4" />
              {publishing ? 'Publishing…' : 'Publish to Discover'}
            </button>
          )
        )}
        <div className="flex items-center justify-around gap-2">
        {!isOwner && user && current.creator?.username ? (
          <button
            type="button"
            data-testid="lightbox-mobile-tip"
            onClick={() => {
              // Debug trail — if the user keeps saying "nothing happened", this toast
              // will confirm the button DID fire and the dialog has been requested.
              toast.message(`Opening tip to @${current.creator.username}…`);
              setTipOpen(true);
            }}
            className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl px-3 py-3 bg-amber-500/20 text-amber-100 font-bold text-sm active:scale-95 touch-manipulation"
          >
            <Gift className="w-4 h-4" /> Tip
          </button>
        ) : (
          // Still render a disabled Tip chip so the user never thinks it's missing.
          // Explains WHY it can't be tapped (own creation / anonymous author / not signed in).
          <button
            type="button"
            data-testid="lightbox-mobile-tip-disabled"
            onClick={() => {
              if (!user) return toast.error('Sign in to tip creators');
              if (isOwner) return toast.message("That's your creation — share the link so your fans can tip you!");
              if (!current.creator?.username) return toast.error("This creation doesn't have a creator attached yet.");
            }}
            className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl px-3 py-3 bg-white/5 text-slate-500 font-bold text-sm active:scale-95 touch-manipulation"
            title={isOwner ? 'Your own creation' : (!user ? 'Sign in to tip' : 'No creator info')}
          >
            <Gift className="w-4 h-4" /> Tip
          </button>
        )}
        <button
          type="button"
          data-testid="lightbox-mobile-share"
          onClick={share}
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl px-3 py-3 bg-white/10 text-white font-bold text-sm active:scale-95 touch-manipulation"
        >
          <Share2 className="w-4 h-4" /> Share
        </button>
        <button
          type="button"
          data-testid="lightbox-mobile-download"
          onClick={downloadCreation}
          disabled={downloading}
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl px-3 py-3 gradient-brand text-white font-bold text-sm active:scale-95 touch-manipulation disabled:opacity-60"
        >
          <Download className="w-4 h-4" /> {downloading ? 'Saving…' : 'Download'}
        </button>
        </div>
      </div>

      <PhotoTipDialog
        open={tipOpen}
        onOpenChange={setTipOpen}
        targetUsername={current.creator?.username}
        creationId={current.id}
      />
    </div>
  );
}
